import { useState, useEffect, useRef, useMemo } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { Menu, X, LogOut, Settings, Moon, Sun, Wifi, Users, Gift, BookOpen, Store, DollarSign, ChevronDown, ChevronRight, User, Search, Heart, ShoppingCart, Package, Globe, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import CreateHotspotModal from "@/components/CreateHotspotModal";
import { storeService } from "@/api/services/storeService";
import { useTranslation } from "react-i18next";
import html2canvas from 'html2canvas';

export default function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n: i18nHook } = useTranslation();
  const { user, logout, theme, toggleTheme } = useApp();
  const [langOverride, setLangOverride] = useState(
    localStorage.getItem("i18nextLng")?.toLowerCase().startsWith("fr") ? "fr" : "en"
  );
  const t = i18nHook.getFixedT(langOverride);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [operatorKitOpen, setOperatorKitOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [dragPosition, setDragPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [wishlistCount, setWishlistCount] = useState(0);
  const dragStartX = useRef(0);
  const tr = (key: string, defaultValue: string) =>
    t(key, { defaultValue });

  useEffect(() => {
    const storedLang = localStorage.getItem("i18nextLng");
    if (!storedLang) return;
    const normalized = storedLang.toLowerCase().startsWith("fr") ? "fr" : "en";
    setLangOverride(normalized);
    if (i18nHook.language !== normalized) {
      i18nHook.changeLanguage(normalized);
    }
    const onAppLanguageChanged = (event: Event) => {
      const next = (event as CustomEvent).detail as string;
      const normalizedNext = next?.toLowerCase().startsWith("fr") ? "fr" : "en";
      setLangOverride(normalizedNext);
    };
    window.addEventListener("appLanguageChanged", onAppLanguageChanged as EventListener);
    return () => {
      window.removeEventListener("appLanguageChanged", onAppLanguageChanged as EventListener);
    };
  }, [i18nHook]);

  const fetchWishlistCount = async () => {
  try {
    const { totalItems } = await storeService.getWishlist();
    setWishlistCount(totalItems);
  } catch {
    setWishlistCount(0);
  }
};

useEffect(() => {
  if (location.pathname.startsWith("/operator")) {
    fetchWishlistCount();
  }
}, [location.pathname]);

// Listen for wishlist changes dispatched from Store/Wishlist pages
useEffect(() => {
  const handler = () => fetchWishlistCount();
  window.addEventListener("wishlistUpdated", handler);
  return () => window.removeEventListener("wishlistUpdated", handler);
}, []);

  const isRewardPage = location.pathname === "/operator/Reward";
  const isReferralsPage = location.pathname === "/operator/Referrals";
  const isSetupguidePage = location.pathname === "/operator/setupguide";
  const isStorePage = location.pathname === "/operator/store";
  const isTokenPage = location.pathname === "/operator/Token";
  const isSettingsPage = location.pathname === "/operator/Settings";
  const isDonatePage = location.pathname === "/operator/donate";
  const isSupportDonatePage = location.pathname === "/operator/support-donate";
  const isregisterKitPage = location.pathname === "/operator/registerKit";
  const isregisterKitpageStep2 = location.pathname === "/operator/registerKitStep3";
  const isregisterKitpageStep3 = location.pathname === "/operator/registerKitStep2";
  const Independentoperator = location.pathname === "/operator/independentOperator";
  const HotspotAcess = location.pathname === "/operator/HotspotAcess";
  const Vouchers = location.pathname === "/operator/Vouchers";
  const Finances = location.pathname === "/operator/Finances";
  const Withdrawfunds = location.pathname === "/operator/Withdrawfunds";
  const isAllKitsPage = location.pathname === "/operator/allkits";
  const isCart = location.pathname === "/operator/cart";
  const isOperatorKitPage = location.pathname === "/operator/operatorkit";
  const isManageKitPage = location.pathname === "/operator/managekit";
  const menuItems = useMemo(() => [
    { label: tr("operatoruser.navbar.text", "Hotspots"), path: "/operator/", icon: Wifi },
    { label: tr("operatoruser.navbar.text1", "Referrals"), path: "/operator/Referrals", icon: Users },
    { label: tr("operatoruser.navbar.text2", "Rewards"), path: "/operator/Reward", icon: Gift },
    { label: tr("operatoruser.navbar.text3", "Setup Guide"), path: "/operator/setupguide", icon: BookOpen },
    { label: tr("operatoruser.navbar.text4", "Store"), path: "/operator/store", icon: Store },
    { label: tr("operatoruser.navbar.payouts", "Payouts"), path: "/operator/payouts", icon: DollarSign },
  ], [t]);
  const bottomMenuItems = useMemo(() => [
    { label: tr("operatoruser.navbar.text5", "Settings"), path: "/operator/Settings", icon: Settings },
    { label: tr("operatoruser.navbar.text6", "Token"), path: "/operator/Token", icon: DollarSign },
    { label: tr("operatoruser.navbar.helpsupport", "Help & Support"), path: "/operator/support", icon: MessageSquare },
  ], [t]);
  // Donate menu — backend endpoints live at /api/app/donate. Pages re-export
  // the end-user donate component (CTO confirmed identical behaviour).
  const donateMenuItems = useMemo(
    () => [
      {
        label: tr("operatoruser.navbar.donate", "Donate"),
        path: "/operator/donate",
        matchPaths: ["/operator/donate"],
      },
      {
        label: tr("operatoruser.navbar.supportDonate", "Support Donate"),
        path: "/operator/support-donate",
        matchPaths: ["/operator/support-donate"],
      },
    ] as { label: string; path: string; matchPaths: string[] }[],
    [t]
  );
  const isDonateMenuActive = donateMenuItems.some((item) => item.matchPaths.includes(location.pathname));
  const [isDonateDropdownOpen, setIsDonateDropdownOpen] = useState(isDonateMenuActive);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (isDonateMenuActive) {
      setIsDonateDropdownOpen(true);
    }
  }, [isDonateMenuActive]);

  const fetchCartCount = async () => {
  try {
    const { summary } = await storeService.getCart();
    setCartCount(summary.itemCount);
  } catch (error: unknown) {
    console.warn("Failed to fetch cart count:", error);
    setCartCount(0);
  }
};

  useEffect(() => {
    if (location.pathname.startsWith("/operator")) {
      fetchCartCount();
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleCartUpdated = () => {
      fetchCartCount();
    };
    window.addEventListener("cartUpdated", handleCartUpdated);
    return () => window.removeEventListener("cartUpdated", handleCartUpdated);
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    dragStartX.current = e.touches[0].clientX;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isMobile || !isDragging) return;
    const currentX = e.touches[0].clientX;
    const diff = currentX - dragStartX.current;
    
    if (sidebarOpen) {
      if (diff < 0) {
        setDragPosition(Math.max(diff, -256));
      }
    } else {
      if (dragStartX.current < 20 && diff > 0) {
        setDragPosition(Math.min(diff - 256, 0));
      }
    }
  };

  const handleTouchEnd = () => {
    if (!isMobile || !isDragging) return;
    setIsDragging(false);
    
    if (Math.abs(dragPosition) > 128) {
      setSidebarOpen(!sidebarOpen);
    }
    
    setDragPosition(0);
  };

  const handleHotspotCreated = (hotspot: Record<string, unknown>) => {
    console.log("Hotspot created successfully!", hotspot);
    window.dispatchEvent(new CustomEvent('hotspotCreated', { detail: hotspot }));
  };

  function getInitials(name?: string, email?: string): string {
    if (name) {
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
      } else if (parts.length === 1) {
        return parts[0].substring(0, 2).toUpperCase();
      }
    }
    
    if (email) {
      const emailPrefix = email.split('@')[0];
      const words = emailPrefix.match(/[A-Za-z]+/g) || [];
      if (words.length >= 2) {
        return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
      }
      return emailPrefix.substring(0, 2).toUpperCase();
    }
    
    return 'U';
  }


  const handleMenuClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    navigate(path);
  };

  const handleExportScreenshot = async () => {
    try {
      const element = document.getElementById('dashboard-content-area');
      if (!element) return;
      // Optional: Add a class for a brief loading state, or handle UI feedback
      const canvas = await html2canvas(element, { backgroundColor: null });
      const image = canvas.toDataURL('image/png', 1.0);
      const link = document.createElement('a');
      link.download = `konectx-dashboard-${new Date().getTime()}.png`;
      link.href = image;
      link.click();
    } catch (e: unknown) {
      console.error('Failed to take screenshot:', e);
    }
  };

  return (
    <div className={`flex h-screen overflow-hidden ${theme === 'dark' ? 'bg-black' : 'bg-background'}`}>
      <CreateHotspotModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleHotspotCreated}
      />

      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div
        ref={sidebarRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: isMobile && isDragging ? `translateX(${dragPosition}px)` : undefined,
          width: isMobile ? '256px' : sidebarOpen ? '256px' : '80px',
          minWidth: isMobile ? '256px' : sidebarOpen ? '256px' : '80px',
          maxWidth: isMobile ? '256px' : sidebarOpen ? '256px' : '80px',
        }}
        className={`
          ${isMobile ? 'fixed' : 'relative'}
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${theme === 'dark' ? 'bg-[#333436] border-[#2b2b2c]' : 'bg-gray-100 border-gray-200'}
          border-r flex flex-col h-full z-50
          ${isMobile ? 'top-0 left-0' : ''}
        `}
      >
        <div className={`p-4 ${sidebarOpen || isMobile ? 'h-[76px]' : 'h-[76px]'}`}>
          <div className="flex items-center justify-between h-full">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={`flex items-center ${sidebarOpen || isMobile ? 'gap-3' : 'justify-center w-full'} focus:outline-none hover:opacity-80`}
            >
              {(sidebarOpen || isMobile) ? (
                <img
                  src={theme === 'dark' ? '/KonnectXLogo-dark.png' : '/KonnectXLogo-light.png'}
                  alt="KonectX Logo"
                  className="h-10 object-contain cursor-pointer"
                />
              ) : (
                <img
                  src={theme === 'dark' ? '/icons/favicon-white.png' : '/icons/favicon-blue.png'}
                  alt="KonectX Icon"
                  className="w-12 h-12 object-contain cursor-pointer"
                />
              )}
            </button>
            {isMobile && (
              <button
                onClick={() => setSidebarOpen(false)}
                className={`p-2 rounded-lg ${theme === 'dark' ? 'hover:bg-gray-800' : 'hover:bg-gray-200'}`}
              >
                <X size={20} className={theme === 'dark' ? 'text-white' : 'text-gray-800'} />
              </button>
            )}
          </div>
        </div>
       
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <a
                key={item.path}
                href={item.path}
                onClick={(e) => handleMenuClick(e, item.path)}
                className={`flex items-center ${sidebarOpen || isMobile ? 'gap-3' : 'justify-center'} px-4 py-3 rounded-lg ${
                  isActive
                    ? 'bg-black text-white dark:bg-blue-600'
                    : theme === 'dark'
                      ? 'text-gray-300 hover:bg-blue-600 hover:text-white'
                      : 'text-gray-700 hover:bg-gray-900 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {(sidebarOpen || isMobile) && <span className="font-medium text-base whitespace-nowrap">{item.label}</span>}
              </a>
            );
          })}

          <button
            onClick={() => {
              if (!(sidebarOpen || isMobile)) {
                navigate("/operator/donate");
                return;
              }
              setIsDonateDropdownOpen((prev) => !prev);
            }}
            className={`w-full flex items-center ${sidebarOpen || isMobile ? 'gap-3' : 'justify-center'} px-4 py-3 rounded-lg ${
              isDonateMenuActive
                ? 'bg-black text-white dark:bg-blue-600'
                : theme === 'dark'
                  ? 'text-gray-300 hover:bg-blue-600 hover:text-white'
                  : 'text-gray-700 hover:bg-gray-900 hover:text-white'
            }`}
          >
            <Heart className="w-5 h-5 flex-shrink-0" />
            {(sidebarOpen || isMobile) && (
              <>
                <span className="font-medium text-base whitespace-nowrap flex-1 text-left">
                  {tr("operatoruser.navbar.text11", "Donate")}
                </span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${isDonateDropdownOpen ? "rotate-180" : ""}`}
                />
              </>
            )}
          </button>

          {(sidebarOpen || isMobile) && isDonateDropdownOpen && (
            <div className="space-y-1">
              {donateMenuItems.map((item) => {
                const isActive = item.matchPaths.includes(location.pathname);
                return (
                  <a
                    key={item.path}
                    href={item.path}
                    onClick={(e) => handleMenuClick(e, item.path)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
                      isActive
                        ? 'bg-black text-white dark:bg-blue-600'
                        : theme === 'dark'
                          ? 'text-gray-300 hover:bg-blue-600 hover:text-white'
                          : 'text-gray-700 hover:bg-gray-900 hover:text-white'
                    }`}
                  >
                    <span className="w-2 h-2 rounded-full bg-current opacity-90 flex-shrink-0" />
                    <span className="font-medium text-base whitespace-nowrap">{item.label}</span>
                  </a>
                );
              })}
            </div>
          )}

          {bottomMenuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <a
                key={item.path}
                href={item.path}
                onClick={(e) => handleMenuClick(e, item.path)}
                className={`flex items-center ${sidebarOpen || isMobile ? 'gap-3' : 'justify-center'} px-4 py-3 rounded-lg ${
                  isActive
                    ? 'bg-black text-white dark:bg-blue-600'
                    : theme === 'dark'
                      ? 'text-gray-300 hover:bg-blue-600 hover:text-white'
                      : 'text-gray-700 hover:bg-gray-900 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {(sidebarOpen || isMobile) && <span className="font-medium text-base whitespace-nowrap">{item.label}</span>}
              </a>
            );
          })}

          <div>
            <div className={`w-full flex items-center ${sidebarOpen || isMobile ? 'justify-between gap-3' : 'justify-center'} px-4 py-3 rounded-lg ${
                location.pathname === "/operator/operatorkit" || location.pathname === "/operator/allkits" || location.pathname === "/operator/managekit"
                  ? 'bg-black text-white dark:bg-blue-600'
                  : theme === 'dark'
                    ? 'text-gray-300 hover:bg-blue-600 hover:text-white'
                    : 'text-gray-700 hover:bg-gray-900 hover:text-white'
              }`}>
              <a
                href="/operator/operatorkit"
               
                onClick={(e) => handleMenuClick(e, '/operator/operatorkit')}
                className={`flex items-center ${sidebarOpen || isMobile ? 'gap-3 flex-1' : 'justify-center'}`}
              >
                <Package className="w-5 h-5 flex-shrink-0" />
                {(sidebarOpen || isMobile) && <span className="font-medium text-base whitespace-nowrap">{tr("operatoruser.navbar.text7", "Kit (BYO)")}</span>}
              </a>

              {(sidebarOpen || isMobile) && (
                <button
                  onClick={(e) => { e.stopPropagation(); setOperatorKitOpen((s) => !s); }}
                  aria-expanded={operatorKitOpen}
                  aria-controls="operator-kit-dropdown"
                  className="p-1 rounded focus:outline-none flex-shrink-0"
                >
                  {operatorKitOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                </button>
              )}
            </div>

            {(sidebarOpen || isMobile) && operatorKitOpen && (
              <div id="operator-kit-dropdown" className="ml-4 mt-1 space-y-1">
                <a
                  href="/operator/allkits"
                  onClick={(e) => handleMenuClick(e, '/operator/allkits')}
                  className={`flex items-center gap-3 px-4 py-2 rounded-lg ${
                    location.pathname === "/operator/allkits"
                      ? 'bg-black text-white dark:bg-blue-600'
                      : theme === 'dark'
                        ? 'text-gray-400 hover:bg-blue-600 hover:text-white'
                        : 'text-gray-600 hover:bg-gray-900 hover:text-white'
                  }`}
                >
                  <div className="w-2 h-2 rounded-full bg-current flex-shrink-0" />
                  <span className="font-medium text-sm whitespace-nowrap">{tr("operatoruser.navbar.text8.0", "All Kits")}</span>
                </a>
                <a
                  href="/operator/operatorkit"
                  onClick={(e) => handleMenuClick(e, '/operator/operatorkit')}
                  className={`flex items-center gap-3 px-4 py-2 rounded-lg ${
                    location.pathname === "/operator/operatorkit"
                      ? 'bg-black text-white dark:bg-blue-600'
                      : theme === 'dark'
                        ? 'text-gray-400 hover:bg-blue-600 hover:text-white'
                        : 'text-gray-600 hover:bg-gray-900 hover:text-white'
                  }`}
                >
                  <div className="w-2 h-2 rounded-full bg-current flex-shrink-0" />
                  <span className="font-medium text-sm whitespace-nowrap">{tr("operatoruser.navbar.text8.1", "Manage Kit")}</span>
                </a>
              </div>
            )}
          </div>
        </nav>

        <div className={`border-t ${theme === 'dark' ? 'border-gray-800' : 'border-gray-300'}`}>
          <div className="p-4 pt-2 space-y-2">
            <button
              onClick={toggleTheme}
              className={`w-full flex items-center ${sidebarOpen || isMobile ? 'gap-3' : 'justify-center'} px-4 py-3 rounded-lg ${
                theme === 'dark' 
                  ? 'text-gray-300 hover:bg-gray-800' 
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              {theme === "light" ? <Moon className="w-5 h-5 flex-shrink-0" /> : <Sun className="w-5 h-5 flex-shrink-0" />}
              {(sidebarOpen || isMobile) && <span className="font-medium text-base whitespace-nowrap">{theme === "light" ? tr("operatoruser.navbar.text9.1", "Dark Mode") : tr("operatoruser.navbar.text9.0", "Light Mode")}</span>}
            </button>
          
            <button
              onClick={logout}
              className={`w-full flex items-center ${sidebarOpen || isMobile ? 'gap-3' : 'justify-center'} px-4 py-3 rounded-lg hover:text-red-500 ${
                theme === 'dark' 
                  ? 'text-gray-300 hover:bg-gray-800' 
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              {(sidebarOpen || isMobile) && <span className="font-medium text-base whitespace-nowrap">{tr("operatoruser.navbar.text10", "Logout")}</span>}
            </button>
          </div>

          {(sidebarOpen || isMobile) && user && (
           <Link to="/operator/Settings" className="block">
  <div className={`p-4 border-t cursor-pointer ${
    theme === 'dark' ? 'border-gray-800' : 'border-gray-300 hover:bg-gray-100'
  }`}>
    <div className="flex items-center gap-3">
      {user.avatar ? (
        <img
          src={user.avatar}
          alt={user.displayName || user.name || "User"}
          className="w-10 h-10 rounded-full object-cover flex-shrink-0"
        />
      ) : (
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
          {user.initials || getInitials(user.name, user.email)}
        </div>
      )}

      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold truncate ${
          theme === 'dark' ? 'text-white' : 'text-gray-800'
        }`}>
          {user.displayName || user.name || "User"}
        </p>

        <p className={`text-xs truncate ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
        }`}>
          {user.email}
        </p>
      </div>
    </div>
  </div>
</Link>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden w-full">
        <header className={`px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between relative ${theme === 'dark' ? 'bg-black border-b border-gray-800' : 'bg-gray-50'}`}>
          {isMobile && (
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={`p-2 rounded-lg transition mr-2 ${
                theme === 'dark' ? 'hover:bg-gray-900 text-white' : 'hover:bg-accent text-foreground'
              }`}
            >
              <Menu size={24} />
            </button>
          )}

          {(isOperatorKitPage || isregisterKitPage || isregisterKitpageStep2 || isregisterKitpageStep3 || Independentoperator || HotspotAcess || Vouchers || Finances || Withdrawfunds || isManageKitPage) ? (
            <>
              <h2 className={`text-lg sm:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-foreground'}`}>
                {isOperatorKitPage ? tr("operatoruser.navbar.text7", "Kit") : isManageKitPage ? tr("operatoruser.navbar.text8.1", "Manage Kit") : isregisterKitpageStep2 ? tr("operatoruser.allkit.button1", "Register Kit"): isregisterKitpageStep3 ? tr("operatoruser.allkit.button1", "Register Kit"): ""}
              </h2>

              <div className="flex-1 flex justify-center items-center px-4 sm:px-8 max-w-md mx-auto">
                <button className={`flex items-center justify-center gap-3 px-6 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base font-medium rounded-lg transition w-full ${
                  theme === 'dark'
                    ? 'text-gray-300 bg-gray-900 border border-gray-700 hover:bg-gray-800'
                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                }`}>
                  <Search className="w-5 h-5" />
                  <span>{tr("operatoruser.allkit.searchplaceholder", "Search Kits")}</span>
                </button>
              </div>

              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={() => {
                    const currentIsFr = langOverride === "fr";
                    const next = currentIsFr ? "en" : "fr";
                    localStorage.setItem("i18nextLng", next);
                    setLangOverride(next);
                    document.documentElement.lang = next;
                    i18nHook.changeLanguage(next);
                    window.dispatchEvent(new CustomEvent("appLanguageChanged", { detail: next }));
                  }}
                  className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition border ${
                    theme === 'dark'
                      ? 'text-gray-300 border-gray-700 hover:bg-gray-800'
                      : 'text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                  aria-label={`Switch language to ${langOverride === "fr" ? "English" : "French"}`}
                >
                  <Globe className="w-4 h-4" />
                  <span className="hidden sm:inline">{langOverride === "fr" ? "FR" : "EN"}</span>
                </button>
                <button className={`flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition ${
                  theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'
                }`}>
                  <Heart className="w-5 h-5" />
                  <span className="hidden sm:inline">{tr("operatoruser.managekit.header.button", "KYC")}</span>
                </button>
                 <Link to="/operator/allkits">
                <button className={`flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition ${
                  theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'
                }`}>
                  <ShoppingCart className="w-5 h-5" />
                  <span className="hidden sm:inline">{tr("operatoruser.managekit.header.button1", "Kits")}</span>
                </button>
                </Link>
                <Link to="/operator/Settings">
                <button className={`flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition ${
                  theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'
                }`}>
                  <User className="w-5 h-5" />
                  <span className="hidden sm:inline">{tr("operatoruser.managekit.header.button2", "Account")}</span>
                </button>
                </Link>
              </div>
            </>
          ) : (
            isCart ? "" : <>
              <h2 className={`text-lg sm:text-2xl font-bold truncate ${
                isReferralsPage || isSetupguidePage 
                  ? `flex-1 text-center ${theme === 'dark' ? 'text-white' : 'text-foreground'}` 
                  : theme === 'dark' ? 'text-white' : 'text-foreground'
              }`}>
                {isAllKitsPage ? tr("operatoruser.allkit.header.text", "All Kits") : isRewardPage ? tr("operatoruser.Rewards.header.text", "Rewards") : isReferralsPage ? tr("operatoruser.referrals.header.text1", "Referrals") : isSetupguidePage ? tr("operatoruser.setupguide.header.text", "Setup Guide") : isStorePage ? tr("operatoruser.store.header.text", "Store") : isDonatePage ? tr("operatoruser.donate.header.text", "Donate") : isSupportDonatePage ? tr("operatoruser.supportDonate.header.text", "Support Donate") : isTokenPage ? tr("operatoruser.token.header.text", "Token") : isSettingsPage ? tr("operatoruser.setting.header.text", "Settings") : tr("operatoruser.dashboardOverview.header.text1", "Dashboard Overview")}
              </h2>

              <div className="flex items-center gap-4 sm:gap-6 ml-auto">
                <button
                  onClick={() => {
                    const currentIsFr = langOverride === "fr";
                    const next = currentIsFr ? "en" : "fr";
                    localStorage.setItem("i18nextLng", next);
                    setLangOverride(next);
                    document.documentElement.lang = next;
                    i18nHook.changeLanguage(next);
                    window.dispatchEvent(new CustomEvent("appLanguageChanged", { detail: next }));
                  }}
                  className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition border ${
                    theme === 'dark'
                      ? 'text-gray-300 border-gray-700 hover:bg-gray-800'
                      : 'text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                  aria-label={`Switch language to ${langOverride === "fr" ? "English" : "French"}`}
                >
                  <Globe className="w-4 h-4" />
                  <span className="hidden sm:inline">{langOverride === "fr" ? "FR" : "EN"}</span>
                </button>
                {isRewardPage ? (
                  <>
                     <Link to="/operator/Settings">
                  <button className={`flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition ${
                    theme === 'dark'
                      ? 'text-black bg-blue-700 border border-gray-700'
                      : 'text-white bg-black border border-gray-300'
                  }`}>
                    
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline">{tr("operatoruser.referrals.header.button", "My Account")}</span>
                  </button>
                 </Link>
                    
                  </>
                ) : isSetupguidePage ? (
                  <Link to="/operator/Settings">
                  <button className={`flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition ${
                    theme === 'dark'
                      ? 'text-black bg-blue-700 border border-gray-700'
                      : 'text-white bg-black border border-gray-300'
                  }`}>
                    
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline">{tr("operatoruser.referrals.header.button", "My Account")}</span>
                  </button>
                 </Link>
                ) : isSettingsPage ? (
                  <Link to="/operator/Settings">
                  <button className={`flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition ${
                    theme === 'dark'
                      ? 'text-black bg-blue-700 border border-gray-700'
                      : 'text-white bg-black border border-gray-300'
                  }`}>
                    
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline">{tr("operatoruser.referrals.header.button", "My Account")}</span>
                  </button>
                 </Link>
                ) : isStorePage ? (
                  <>
                 <button
        onClick={() => navigate("/operator/wishlist")}
        className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition ${
          theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'
        }`}
      >
        <Heart className="w-4 h-4" />
        <span className="hidden sm:inline">{tr("operatoruser.store.header.wishlist", "Wishlist")}</span>
        {wishlistCount > 0 && (
          <span className="ml-1 text-xs font-semibold">{wishlistCount}</span>
        )}
      </button>
                    <button
                      onClick={() => navigate("/operator/cart")}
                      className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}
                    >
                      <ShoppingCart className="w-4 h-4" />
                      <span className="hidden sm:inline">{tr("operatoruser.store.header.text2", "Cart")}</span>
                      {cartCount}
                    </button>
                     <Link to="/operator/profile">
                    <button className={`flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      <User className="w-4 h-4" />
                      <span className="hidden sm:inline">{tr("operatoruser.store.header.text3", "Account")}</span>
                    </button>
                    </Link>
                  </>
                ) : isReferralsPage ? (
                   <Link to="/operator/Settings">
                  <button className={`flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition ${
                    theme === 'dark'
                      ? 'text-black bg-blue-700 border border-gray-700'
                      : 'text-white bg-black border border-gray-300'
                  }`}>
                    
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline">{tr("operatoruser.referrals.header.button", "My Account")}</span>
                  </button>
                 </Link>
                ) : isDonatePage || isSupportDonatePage ? (
                  <Link to="/operator/Settings">
                  <button className={`flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition ${
                    theme === 'dark'
                      ? 'text-black bg-blue-700 border border-gray-700'
                      : 'text-white bg-black border border-gray-300'
                  }`}>
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline">{tr("operatoruser.referrals.header.button", "My Account")}</span>
                  </button>
                 </Link>
                ) : (
                  <>
                    <button 
                      onClick={handleExportScreenshot}
                      className={`hidden sm:flex px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition ${
                      theme === 'dark'
                        ? 'text-blue-500 bg-gray-900 border border-blue-500 hover:bg-gray-800'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}>
                      {tr("operatoruser.dashboardOverview.header.button1", "Export")}
                    </button>
                    <button
                      onClick={() => setIsModalOpen(true)}
                      className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition whitespace-nowrap ${
                        theme === 'dark'
                          ? 'text-black bg-blue-500 hover:bg-blue-600'
                          : 'text-white bg-gray-900 hover:bg-gray-800'
                      }`}
                    >
                      <span className="hidden sm:inline">+ {tr("operatoruser.dashboardOverview.header.button2", "Add Hotspot")}</span>
                      <span className="sm:hidden">+ {tr("operatoruser.dashboardOverview.header.button3", "Add")}</span>
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </header>

        <main id="dashboard-content-area" className={`flex-1 overflow-auto ${theme === 'dark' ? 'bg-black' : 'bg-gray-50'}`}>
         
            <Outlet />
      
        </main>
      </div>
    </div>
  );
}
